/* ═══════════════════════════════════════════════════════════
   PEAKVIEW ALPINE — shared off-homepage site chrome

   Loaded by layout/theme.liquid on every route EXCEPT the index
   template. The homepage keeps its approved main.js implementation
   untouched; this file never runs there.

   Scope is the shared header and footer only:
     · mount the real brand mark
     · header scrolled/background state
     · mobile menu open / close
     · focus trap, Escape, focus restoration
     · aria-expanded + accessible button label
     · close on menu destination
     · close when the viewport crosses back to desktop

   Deliberately NOT here: page-specific behaviour of any kind, commerce
   logic, form logic, animation libraries, scroll loops and
   requestAnimationFrame loops.

   Selectors and behaviour mirror the proven implementation in
   assets/main.js (initNav) so the two routes behave the same. Element
   lookups are deferred and click/key handling is delegated at document
   level, so a Shopify section re-render can never leave a stale
   reference or a duplicated listener behind.
   ═══════════════════════════════════════════════════════════ */
(function () {
  "use strict";

  if (window.__pvChromeBooted) return;
  window.__pvChromeBooted = true;

  var doc = document;
  var here = doc.currentScript;

  var menuOpen = false;
  var navEl = null;

  function byId(id) {
    return doc.getElementById(id);
  }

  function nav() {
    if (!navEl || !doc.contains(navEl)) navEl = byId("nav");
    return navEl;
  }

  function menuEl() {
    return byId("menu");
  }

  function burgerEl() {
    return byId("burger");
  }

  /* the regions made inert while the overlay is open */
  function background() {
    var out = [];
    var main = byId("main");
    var foot = doc.querySelector("footer");
    if (main) out.push(main);
    if (foot) out.push(foot);
    return out;
  }

  /* ── brand mark ────────────────────────────── */
  /* The header and footer ship a placeholder <svg class="brand-mark"> that
     assets/logo.js swaps for the real asset. logo.js is imported by main.js on
     the homepage only, and .brand-mark carries filter: invert(1) for that real
     (black-on-transparent) asset — so without the swap the placeholder renders
     inverted and near-invisible on the dark background. Resolved against this
     file's own URL so the Shopify CDN path is correct. Silent on failure. */
  function mountBrand() {
    if (!here || !here.src || typeof window.__pvLogoSrc !== "string") return;
    import(new URL("logo.js", here.src).href)
      .then(function (mod) {
        if (mod && typeof mod.mountLogo === "function") mod.mountLogo();
      })
      .catch(function () {
        /* placeholder mark stays — nothing else depends on it */
      });
  }

  /* ── header background state ───────────────── */
  /* Same threshold as main.js. The homepage's hide-on-scroll (nav--hidden) is
     intentionally NOT reproduced: on inner pages the header should stay
     available rather than retract. */
  function applyNavState() {
    var n = nav();
    if (!n) return;
    var y = window.pageYOffset || doc.documentElement.scrollTop || 0;
    n.classList.toggle("nav--scrolled", y > 40);
  }

  /* ── mobile menu ───────────────────────────── */
  function openMenu() {
    var menu = menuEl();
    var burger = burgerEl();
    if (!menu || !burger || menuOpen) return;
    menuOpen = true;

    burger.classList.add("open");
    burger.setAttribute("aria-expanded", "true");
    burger.setAttribute("aria-label", "Close menu");

    menu.setAttribute("aria-hidden", "false");
    menu.classList.add("open");
    /* .menu.open restores visibility only — opacity stays 0 in the stylesheet
       and is supplied by script on both routes (main.js does the same on its
       no-GSAP path). Without this the overlay is present but invisible. */
    menu.style.opacity = "1";

    background().forEach(function (el) {
      el.setAttribute("inert", "");
      el.setAttribute("aria-hidden", "true");
    });

    var links = menu.querySelectorAll(".menu-link");
    if (links.length) links[0].focus();
  }

  function closeMenu() {
    if (!menuOpen) return;
    menuOpen = false;

    var menu = menuEl();
    var burger = burgerEl();

    if (burger) {
      burger.classList.remove("open");
      burger.setAttribute("aria-expanded", "false");
      burger.setAttribute("aria-label", "Open menu");
    }

    if (menu) {
      menu.setAttribute("aria-hidden", "true");
      menu.classList.remove("open");
      menu.style.opacity = "";
    }

    background().forEach(function (el) {
      el.removeAttribute("inert");
      el.removeAttribute("aria-hidden");
    });

    /* focus returns to the control that opened the overlay */
    if (burger && typeof burger.focus === "function") burger.focus();
  }

  function toggleMenu() {
    if (menuOpen) closeMenu();
    else openMenu();
  }

  /* ── delegated listeners — bound once, survive section re-renders ── */
  doc.addEventListener("click", function (e) {
    var t = e.target;
    if (!t || typeof t.closest !== "function") return;

    if (t.closest("#burger")) {
      e.preventDefault();
      toggleMenu();
      return;
    }

    /* selecting a destination closes the overlay */
    if (menuOpen && t.closest("#menu a")) closeMenu();
  });

  doc.addEventListener("keydown", function (e) {
    if (!menuOpen) return;

    if (e.key === "Escape" || e.key === "Esc") {
      e.preventDefault();
      closeMenu();
      return;
    }
    if (e.key !== "Tab") return;

    var menu = menuEl();
    if (!menu) return;
    var links = menu.querySelectorAll(".menu-link");
    if (!links.length) return;

    var first = links[0];
    var last = links[links.length - 1];
    var active = doc.activeElement;

    if (e.shiftKey && (active === first || !menu.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !menu.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  });

  window.addEventListener("scroll", applyNavState, { passive: true });

  /* crossing back to desktop retires the overlay — the burger is hidden at
     min-width: 1025px, so an open menu would otherwise be unclosable */
  var desktop = window.matchMedia("(min-width: 1025px)");
  function onBreakpoint(e) {
    if (e.matches && menuOpen) closeMenu();
  }
  if (typeof desktop.addEventListener === "function") desktop.addEventListener("change", onBreakpoint);
  else if (typeof desktop.addListener === "function") desktop.addListener(onBreakpoint);

  /* ── boot ──────────────────────────────────── */
  /* listeners above are already bound, so a failure here can never leave the
     menu unusable */
  try {
    applyNavState();
    mountBrand();
  } catch (err) {
    /* chrome degrades to its static state — no content depends on it */
  }

  /* ── Shopify Theme Editor (design mode only) ── */
  /* The header/footer groups can be re-rendered under us. Nothing needs
     re-binding (all handling is delegated), so this only resets transient
     state and re-mounts the brand mark in the fresh markup. */
  if (window.Shopify && window.Shopify.designMode) {
    doc.addEventListener("shopify:section:load", function () {
      if (menuOpen) closeMenu();
      navEl = null;
      applyNavState();
      mountBrand();
    });
  }
})();
